import { Component, OnInit, ViewChild } from '@angular/core';
import { LinkComponent, NavTarget } from '../../../../shared/components/link/link.component';
import { ActivatedRoute, Router } from '@angular/router';
import { GbpersonService, PersonGbSearchStandardRequest, PersonStammMasterListEntryMeta } from '../../../../core/gb-api';
import { DayMonthYearDatePipe } from '../../../../shared/pipes/simple/dayMonthYearDate.pipe';
import { submit } from '../../persongb.functions';
import { FormsModule, NgForm } from '@angular/forms';
import { FormatPersonstammAddressPipe } from '../../../../shared/pipes/format/format-personstamm-address.pipe';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { keysToTranslate } from '../../../../../i18n/keys-to-translate';
import { SearchStateService } from '../../../../shared/services/search-state.service';
import { SearchMode } from '../../../../shared/enum/searchMode';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { PersonStammListEntryMeta, PersonStammSearchResponseMeta } from '../../../../core/personstamm-api';
import { NgbPopover, NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { AbstractTypeaheadSearch } from '../../../../shared/components/typeahead-search/abstract-typeahead-search';
import { CapFormRowDirective } from '../../../../shared/form-control/cap-form-row.directive';
import { MetaDisablerDirective } from '../../../../shared/directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../../../shared/directives/meta-validator.directive';

@Component({
  selector: 'cap-persongb-suche-standard',
  templateUrl: './persongb-suche-standard.component.html',
  styleUrls: ['./persongb-suche-standard.component.scss'],
  imports: [FormsModule, CapFormRowDirective, NgbTypeahead, MetaDisablerDirective, MetaValidatorDirective, NgbPopover, TranslatePipe]
})
export class PersongbSucheStandardComponent extends AbstractTypeaheadSearch<PersonStammMasterListEntryMeta> implements OnInit {
  @ViewChild('form', { static: false })
  form: NgForm;

  showInputAfterSelect = false;
  minSearchLength = 3;
  debounceTime = 500;
  isSearching = false;

  model: PersonStammMasterListEntryMeta;

  constructor(
    private stateService: SearchStateService,
    private personGbService: GbpersonService,
    private route: ActivatedRoute,
    private router: Router,
    private dayMonthYearDatePipe: DayMonthYearDatePipe,
    private formatAdressPipe: FormatPersonstammAddressPipe,
    private translateService: TranslateService
  ) {
    keysToTranslate(['EingabeDarfNichtLeerSein']);
    super();
  }

  ngOnInit() {}

  onSelected($event: NgbTypeaheadSelectItemEvent<PersonStammMasterListEntryMeta>) {
    const selectedItem: PersonStammMasterListEntryMeta = $event.item;
    this.selectedItem = selectedItem;
    if (selectedItem) {
      this.stateService.lastSearchMode = SearchMode.searchStandard;
      this.model = selectedItem;
      this.value = this.formatEntry(selectedItem);
      const path = LinkComponent.getPath(NavTarget.NAV_PERSON_STAMM);
      this.router.navigate([path, selectedItem.masterId.id], {
        relativeTo: this.route
      });
    } else {
      this.value = '';
    }
  }

  onSubmit() {
    if (this.value) {
      const request = this.createRequestObject(this.value);
      submit(this.form, 'personGbStandard', request, this.router, this.route, '../person/list');
    }
  }

  search = (search$: Observable<string>) => {
    return search$.pipe(
      debounceTime(this.debounceTime),
      distinctUntilChanged(),
      switchMap((term) => {
        this.searchTerm = term;
        if (term.length >= this.minSearchLength) {
          this.isSearching = true;
          const request = this.createRequestObject(term);
          return this.personGbService.searchPersonGbStandard(request);
        }
        return of({});
      }),
      map((data: PersonStammSearchResponseMeta) => {
        this.isSearching = false;
        const results = this.findMatches(data);
        this.results = results;
        return results;
      }),
      catchError((err) => {
        this.isSearching = false;
        // prevent alerting and location.back() of SearchResultValidationExceptionHandler
        if (err.status === 400 && err.error && err.error.results) {
          return EMPTY;
        } else {
          throw err;
        }
      })
    );
  };

  private findMatches(data: PersonStammSearchResponseMeta) {
    const matches = data?.results ? data.results.map((searchResult: PersonStammMasterListEntryMeta) => searchResult) : [];
    const withTerm = matches.filter((match) => this.containsTerm(match));
    const withoutTerm = matches.filter((match) => !this.containsTerm(match));
    return withTerm.concat(withoutTerm);
  }

  private createRequestObject(searchQuery: string): PersonGbSearchStandardRequest {
    this.searchTerm = searchQuery;
    return { suchbegriff: searchQuery, rolle: PersonGbSearchStandardRequest.RolleEnum.ALLE };
  }

  private containsTerm(item: PersonStammMasterListEntryMeta): boolean {
    const title = this.formatTitle(item);
    const description = this.formatDescription(item);
    const text = title + ' ' + description;
    if (text && this.searchTerm) {
      return text.toLowerCase().includes(this.searchTerm.toLowerCase());
    }
    return false;
  }

  formatEntry(masterEntry: PersonStammMasterListEntryMeta): string {
    const title = this.formatTitle(masterEntry);
    const description = this.formatDescription(masterEntry);
    return title + ' ' + description;
  }

  private formatTitle(master: PersonStammMasterListEntryMeta): String {
    const entry = master.version;
    if (entry.art === PersonStammListEntryMeta.ArtEnum.NATUERLICHE_PERSON) {
      let value = entry.name + ' ' + entry.vorname;
      if (entry.ledigname) {
        value = value + '(' + entry.ledigname + ')';
      }
      if (entry.geburtsdatum) {
        value = value + ', ' + this.dayMonthYearDatePipe.transform(entry.geburtsdatum);
      }
      return value;
    } else if (entry.art === PersonStammListEntryMeta.ArtEnum.JURISTISCHE_PERSON) {
      return entry.name + ', ' + entry.sitz + ' (' + entry.uid + ')';
    } else {
      return entry.name;
    }
  }

  private formatDescription(master: PersonStammMasterListEntryMeta) {
    const desc = [];
    for (const address of master.adressen) {
      desc.push(this.translateService.instant(address.personAdresseArt) + ': ' + this.formatAdressPipe.transform(null, address));
    }
    return desc.join('\r\n');
  }
}
